import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterIcon from '../../SVG/Filter';
import EmptyHeader from '../Shared/EmptyHeader';
import PDFViewer from '../Shared/PDFViewer';
import { useAuth } from '../../src/Context/AuthContext';

const ImageViewerSection = ({ rowsPerPage = 10 }) => {
    // Auth
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();

    const location = useLocation();
    const passedPolNo = location.state?.polNo || "";

    // --- Filters ---
    const [filterPolNoInput, setFilterPolNoInput] = useState(passedPolNo);
    const [filterStartDateInput, setFilterStartDateInput] = useState('');
    const [filterEndDateInput, setFilterEndDateInput] = useState('');
    const [filterDocTypeInput, setFilterDocTypeInput] = useState('');

    // --- Documents ---
    const [documents, setDocuments] = useState([]);
    const [searchPerformed, setSearchPerformed] = useState(false);

    // --- Sorting ---
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // --- Pagination ---
    const [currentPage, setCurrentPage] = useState(1);

    // --- Modal ---
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);

    // --- Document Types ---
    const [docTypes, setDocTypes] = useState([]);
    const [loadingDocTypes, setLoadingDocTypes] = useState(true);

    const apiURL = import.meta.env.VITE_API;
    const companyName = import.meta.env.VITE_COMPANY_NAME;

    document.title = `${companyName} - Images`;

    // Redirect if user role is >= 3
    useEffect(() => {
        if (!loading) { // only check after context finishes loading
            if (!user || user.hasImage != "true") {
                navigate('/home');
            }
        }
    }, [user, loading, navigate]);

    // Fetch doc types on load
    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const res = await fetch(`${apiURL}/Image/DocumentTypes`, {
                    method: "GET",
                    credentials: "include"
                });

                if (res.status === 401) { logout(); navigate("/login"); return; }
                if (res.status === 403) { navigate("/home"); return; }

                const data = await res.json();
                setDocTypes(data.docTypes || []);
            } catch {
                alert("Could not fetch document types.");
            } finally {
                setLoadingDocTypes(false);
            }
        };

        fetchTypes();
    }, []);

    // Auto-search when coming in with a polNo
    useEffect(() => {
        if (passedPolNo) {
            handleSubmitFilters(new Event("submit"));
        }
    }, [passedPolNo]);

    // --- Sorting Logic ---
    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const sortedDocs = useMemo(() => {
        if (!sortConfig.key) return [...documents];

        const sorted = [...documents].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? "";
            let bVal = b[sortConfig.key] ?? "";

            // Basic date handling
            if (sortConfig.key.toLowerCase().includes("date")) {
                aVal = aVal ? new Date(aVal) : new Date(0);
                bVal = bVal ? new Date(bVal) : new Date(0);
                return aVal - bVal;
            }

            return aVal.toString().localeCompare(
                bVal.toString(),
                undefined,
                { numeric: true }
            );
        });

        return sortConfig.direction === "asc" ? sorted : sorted.reverse();
    }, [documents, sortConfig]);

    // --- Pagination based on sorted docs ---
    const totalEntries = sortedDocs.length;
    const totalPages = Math.ceil(Math.max(totalEntries, 1) / rowsPerPage);

    const paginatedDocs = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return sortedDocs.slice(start, start + rowsPerPage);
    }, [sortedDocs, currentPage, rowsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [documents]);

    // API Call
    const fetchDocuments = async (filters) => {
        try {
            const res = await fetch(`${apiURL}/Image/search`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(filters)
            });

            if (res.status === 401) { logout(); navigate("/login"); return []; }
            if (res.status === 403) { navigate("/home"); return []; }

            const data = await res.json();
            return data.documents || [];
        } catch {
            alert("Search failed.");
            return [];
        }
    };

    const handleSubmitFilters = async (e) => {
        e.preventDefault();
        const filters = {
            polNo: filterPolNoInput.trim() || null,
            startDate: filterStartDateInput || null,
            endDate: filterEndDateInput || null,
            docType: filterDocTypeInput || null,
        };

        const results = await fetchDocuments(filters);
        setDocuments(results);
        setSearchPerformed(true);
    };

    const handleClearFilters = () => {
        setFilterPolNoInput('');
        setFilterStartDateInput('');
        setFilterEndDateInput('');
        setFilterDocTypeInput('');
        setDocuments([]);
        setSearchPerformed(false);
    };

    // PDF
    const getPDF = async (id) => {
        try {
            const res = await fetch(`${apiURL}/image/pdf/view?documentID=${id}`, {
                method: "GET",
                credentials: "include"
            });

            if (!res.ok) throw new Error();
            return await res.blob();
        } catch {
            alert("Unable to fetch PDF");
            return null;
        }
    };

    const handleView = async (doc) => {
        const blob = await getPDF(doc.id);
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setSelectedDoc(doc);
        setShowModal(true);
    };

    const closeModal = () => {
        if (pdfBlobUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
        setShowModal(false);
        setPdfBlobUrl(null);
        setSelectedDoc(null);
    };

    const sortArrow = (col) => {
        if (sortConfig.key !== col) return "";
        return sortConfig.direction === "asc" ? " ▲" : " ▼";
    };

    const from = totalEntries ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);


    // Download file

    const downloadFileWithAuth = async (doc) => {
        try {
            // Step 1: authorize + log download
            const res = await fetch(
                `${apiURL}/image/pdf/authorize-download?documentID=${doc.id}`,
                {
                    method: "POST",
                    credentials: "include",
                }
            );

            if (res.status === 401) {
                logout();
                navigate("/login");
                return;
            }

            if (res.status === 403) {
                navigate("/home");
                return;
            }

            if (!res.ok) {
                alert("Download not authorized.");
                return;
            }

            // Step 2: fetch actual file
            const fileRes = await fetch(
                `${apiURL}/image/pdf/view?documentID=${doc.id}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            if (!fileRes.ok) throw new Error();

            const blob = await fileRes.blob();

            // Step 3: trigger download
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.fileName || "download";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (err) {
            console.error("Download error:", err);
            alert("Unable to download file.");
        }
    };

    // ---------------- RENDER ----------------
    return (
        <>
            <EmptyHeader />
            <div className="container container-section" id="documents-container">

                {/* Filters */}
                <form className="filter-box" onSubmit={handleSubmitFilters}>
                    <FilterIcon />
                    <div className="filter-item">
                        <label className="filterLabel">Reference No:</label>
                        <input
                            type="text"
                            value={filterPolNoInput}
                            className="filterInput"
                            onChange={(e) => setFilterPolNoInput(e.target.value)}
                            placeholder="Filter by RefNo"
                            required
                            maxLength="12"
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Start Date:</label>
                        <input
                            type="date"
                            className="filterInput"
                            value={filterStartDateInput}
                            onChange={(e) => setFilterStartDateInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">End Date:</label>
                        <input
                            type="date"
                            className="filterInput"
                            value={filterEndDateInput}
                            onChange={(e) => setFilterEndDateInput(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Document Type:</label>
                        {loadingDocTypes ? (
                            <select disabled className="filterInput">
                                <option>Loading...</option>
                            </select>
                        ) : (
                            <select
                                className="filterInput"
                                value={filterDocTypeInput}
                                onChange={(e) => setFilterDocTypeInput(e.target.value)}
                            >
                                <option value="">All</option>
                                {docTypes.map((dt) => (
                                    <option key={dt.documentTypeID} value={dt.documentTypeID}>
                                        {dt.documentTypeDesc}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="filter-button-box">
                        <button type="submit" className="filterButton">Search</button>
                        <button type="button" className="filterButton" onClick={handleClearFilters}>Clear</button>
                    </div>
                </form>

                {/* Results */}
                {!searchPerformed ? (
                    <div className="card notfoundbox rounded-3 shadow-lg">
                        <div className="card-body">Please enter search criteria.</div>
                    </div>
                ) : documents.length === 0 ? (
                    <div className="card notfoundbox rounded-3 shadow-lg">
                        <div className="card-body">No documents found.</div>
                    </div>
                ) : (
                    <>
                        <table className="policy-results-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort("refNo")}>
                                        Ref No{sortArrow("refNo")}
                                    </th>
                                    <th onClick={() => handleSort("fileName")}>
                                        Document Name{sortArrow("fileName")}
                                    </th>
                                    <th onClick={() => handleSort("scanDate")}>
                                        Date{sortArrow("scanDate")}
                                    </th>
                                    <th onClick={() => handleSort("documentTypeDesc")}>
                                        Image Type{sortArrow("documentTypeDesc")}
                                    </th>
                                    <th onClick={() => handleSort("totalPageCount")}>
                                        Total Page Count{sortArrow("totalPageCount")}
                                    </th>
                                    <th onClick={() => handleSort("cashReceived")}>
                                        Check Amount{sortArrow("cashReceived")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedDocs.map((doc, index) => {
                                    const isPdf = doc.fileName?.toLowerCase().endsWith('.pdf');

                                    return (
                                        <tr
                                            key={index}
                                            onClick={() => {
                                                if (isPdf) {
                                                    handleView(doc);
                                                } else {
                                                    downloadFileWithAuth(doc);
                                                }
                                            }}
                                            className="clickable-row"
                                        >
                                            <td>{doc.refNo}</td>
                                            <td className="linkText">
                                                {doc.fileName}
                                            </td>
                                            <td>{doc.scanDate}</td>
                                            <td>{doc.documentTypeDesc}</td>
                                            <td>{doc.totalPageCount}</td>
                                            <td>{doc.cashReceived}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div className="pagination-bar">
                            <div className="pagination-summary">
                                Showing {from}-{to} of {totalEntries}
                            </div>
                            <div className="pagination-controls">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                    &#171;
                                </button>
                                <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                                    &#8592;
                                </button>

                                {(() => {
                                    const pages = [];

                                    // Previous page
                                    if (currentPage > 1) pages.push(currentPage - 1);

                                    // Current page
                                    pages.push(currentPage);

                                    // Next page
                                    if (currentPage < totalPages) pages.push(currentPage + 1);

                                    return pages.map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={currentPage === page ? 'active' : ''}
                                        >
                                            {page}
                                        </button>
                                    ));
                                })()}

                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    &#8594;
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    &#187;
                                </button>
                            </div>
                        </div>

                        {/* PDF Modal */}
                        {showModal && pdfBlobUrl && selectedDoc && (
                            <div className="pdf-modal-overlay">
                                <button className="pdf-modal-close" onClick={closeModal}>x Close</button>
                                <div className="pdf-modal-content">
                                    <PDFViewer
                                        src={pdfBlobUrl}
                                        fileId={selectedDoc.id}
                                        fileName={selectedDoc.fileName}
                                        serviceForm={false}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ImageViewerSection;
